import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAppState } from "../context/AppState";
import BottomNav from "../components/BottomNav";
import AddressSheet from "../components/AddressSheet";
import PromoBanner from "../components/PromoBanner";
import { useLocale } from "../context/Locale";

const fallbackServices = [
  {
    id: "POINT_TO_POINT",
    enabled: true,
    name: "Recojo y entrega",
    description: "Llevamos un paquete desde el punto A hasta el punto B.",
    icon: "📦",
  },
  {
    id: "TAXI",
    enabled: false,
    name: "Taxi",
    description: "Próximamente",
    icon: "🚕",
  },
];

function normalizeBanners(payload) {
  const slides = Array.isArray(payload?.mainSlides) ? payload.mainSlides : [];
  return slides
    .map((slide) => ({
      image: String(slide?.data?.image || "").trim(),
      title: String(slide?.data?.name || "Promoción").trim(),
      url: String(slide?.url || "").trim(),
      mediaType: String(slide?.data?.media_type || "IMAGE").toUpperCase(),
    }))
    .filter((banner) => banner.image);
}

export default function PortalHome({ initialDesktopAppPath = "/inicio" }) {
  const navigate = useNavigate();
  const { address, locating, openAddress } = useAppState();
  const { locale, t } = useLocale();
  const [portalEnabled, setPortalEnabled] = useState(null);
  const [services, setServices] = useState(fallbackServices);
  const [banners, setBanners] = useState([]);
  const [desktopSettings, setDesktopSettings] = useState({});
  const [featuredStores, setFeaturedStores] = useState([]);
  const [desktopAppPath, setDesktopAppPath] = useState(initialDesktopAppPath);
  const storeCarouselRef = useRef(null);

  useEffect(() => {
    api("/service-hub")
      .then((data) => {
        if (data?.enabled === false) {
          setPortalEnabled(false);
          navigate("/food", { replace: true });
          return;
        }
        setPortalEnabled(true);
        const configured = Array.isArray(data?.services) ? data.services : [];
        if (!configured.length) return;
        setServices(
          configured.map((service) => ({
            ...service,
            icon: service.id === "TAXI" ? "🚕" : "📦",
          })),
        );
      })
      .catch(() => setPortalEnabled(true));
  }, [navigate]);

  useEffect(() => {
    api("/get-settings", { method: "POST" })
      .then((rows) => {
        const settings = {};
        (Array.isArray(rows) ? rows : []).forEach((row) => {
          settings[row.key] = row.value;
        });
        setDesktopSettings(settings);
      })
      .catch(() => {});

    api("/home-store-logos")
      .then((rows) => setFeaturedStores(Array.isArray(rows) ? rows : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    api("/promo-slider", {
      method: "POST",
      body: JSON.stringify({
        latitude: address?.lat ?? null,
        longitude: address?.lng ?? null,
      }),
    })
      .then((data) => setBanners(normalizeBanners(data)))
      .catch(() => setBanners([]));
  }, [address?.lat, address?.lng]);

  useEffect(() => {
    if (featuredStores.length <= 5) return undefined;
    const timer = window.setInterval(() => scrollStores(1), 2200);
    return () => window.clearInterval(timer);
  }, [featuredStores.length]);

  const serviceMap = useMemo(
    () => Object.fromEntries(services.map((service) => [service.id, service])),
    [services],
  );

  const carouselStores = useMemo(
    () => (featuredStores.length > 5 ? [...featuredStores, ...featuredStores] : featuredStores),
    [featuredStores],
  );

  function openService(serviceId) {
    const service = serviceMap[serviceId];
    if (!service?.enabled) return;
    navigate(`/services/${serviceId.toLowerCase()}`);
  }

  function desktopText(key, fallback) {
    const localizedKey = `${key}_${locale.toLowerCase()}`;
    const configured = locale === "ES"
      ? desktopSettings[key]
      : desktopSettings[localizedKey];
    const value = String(configured || "").trim();
    return value && !/^(null|undefined)$/i.test(value) ? value : fallback;
  }

  function openDesktopApp(path = "/food") {
    setDesktopAppPath(path);
    window.requestAnimationFrame(() => {
      document.querySelector(".desktop-portal__device")?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    });
  }

  function scrollStores(direction) {
    const carousel = storeCarouselRef.current;
    const firstCard = carousel?.querySelector(".desktop-stores__card");
    if (!carousel || !firstCard) return;

    const step = firstCard.getBoundingClientRect().width + 16;
    const loopWidth = featuredStores.length > 5 ? carousel.scrollWidth / 2 : carousel.scrollWidth;

    if (featuredStores.length > 5) {
      if (direction > 0 && carousel.scrollLeft >= loopWidth - step * 1.5) {
        carousel.scrollLeft = 0;
      } else if (direction < 0 && carousel.scrollLeft <= step / 2) {
        carousel.scrollLeft = loopWidth;
      }
    }

    carousel.scrollBy({ left: direction * step, behavior: "smooth" });
  }

  const configuredTitleSize = Number(desktopSettings.portalIntroTitleSize);
  const portalIntroTitleSize = Number.isFinite(configuredTitleSize)
    ? Math.min(64, Math.max(28, configuredTitleSize))
    : 38;

  if (portalEnabled !== true) return null;

  return (
    <main
      className="portal"
      style={{ "--portal-intro-title-size": `${portalIntroTitleSize}px` }}
    >
      <section className="desktop-portal" aria-label="Portada de Delivery Huaraz para computadora">
        <header className="desktop-portal__header">
          <Link to="/inicio" className="desktop-portal__brand">
            <img src="/assets/img/logos/logo.png" alt="Delivery Huaraz" />
          </Link>
          <nav aria-label="Accesos principales">
            <a href="#servicios">{t("services")}</a>
            <a href="#negocios">{t("navBusinesses")}</a>
            <a href="#unete">{t("navJoin")}</a>
          </nav>
          <button type="button" onClick={openAddress}>
            <span>⌖</span>
            <b>{locating ? t("locating") : address?.address || t("selectAddress")}</b>
          </button>
        </header>

        <div className="desktop-portal__hero">
          <div className="desktop-portal__copy">
            <span className="desktop-portal__eyebrow">
              {desktopText("storeName", "Delivery Huaraz").toUpperCase()}
            </span>
            <h1>{desktopText("desktopWhiteLabelHeading", t("heroTitle"))}</h1>
            <p>{desktopText("desktopWhiteLabelDescription", t("heroDescription"))}</p>
            <div className="desktop-portal__steps">
              <div><i>1</i><b>{t("choose")}</b><small>{t("favoriteBusiness")}</small></div>
              <div><i>2</i><b>{t("order")}</b><small>{t("easySecure")}</small></div>
              <div><i>3</i><b>{t("receive")}</b><small>{t("atYourLocation")}</small></div>
            </div>
            <button className="desktop-portal__cta" type="button" onClick={() => openDesktopApp("/food")}>
              {desktopText("desktopWhiteLabelButton", t("exploreFromPc"))} <span>→</span>
            </button>
            <div className="desktop-portal__trust">
              <span>✓ {desktopText("desktopWhiteLabelTrustOne", t("localBusinesses"))}</span>
              <span>✓ {desktopText("desktopWhiteLabelTrustTwo", t("deliveryInHuaraz"))}</span>
              <span>✓ {desktopText("desktopWhiteLabelTrustThree", t("orderTracking"))}</span>
            </div>
          </div>

          <div className="desktop-portal__device">
            <div className="desktop-portal__live"><i /><b>{desktopText("desktopPhoneBadgeTitle", t("liveApp"))}</b><small>{desktopText("desktopPhoneBadgeSubtitle", t("shopHere"))}</small></div>
            <div className={`desktop-portal__phone ${desktopAppPath ? "is-open" : ""}`}>
              <div className="desktop-portal__notch" />
              {desktopAppPath ? (
                <iframe src={desktopAppPath} title="Aplicación Delivery Huaraz" />
              ) : (
                <>
                  <div className="desktop-portal__phone-header">
                    <img src="/assets/img/logos/logo.png" alt="" />
                    <button type="button" onClick={openAddress}>{address?.address || t("selectAddress")} ›</button>
                  </div>
                  <PromoBanner className="desktop-portal__banner" items={banners} />
                  <h2>{desktopText("portalIntroTitle", t("exploreHuaraz"))}</h2>
                  <div className="desktop-portal__phone-services">
                    <button type="button" onClick={() => openDesktopApp("/food")}><span>🍔</span><b>{t("restaurants")}</b></button>
                    <button type="button" onClick={() => openDesktopApp("/services/point_to_point")}><span>📦</span><b>{t("shipments")}</b></button>
                    <button type="button" disabled={!serviceMap.TAXI?.enabled} onClick={() => openDesktopApp("/services/taxi")}><span>🚕</span><b>Taxi</b></button>
                  </div>
                  <button className="desktop-portal__phone-cta" type="button" onClick={() => openDesktopApp("/food")}>{t("openApp")}</button>
                </>
              )}
            </div>
          </div>
        </div>

        <section className="desktop-services" id="servicios">
          <div className="desktop-section-heading">
            <span>{t("allInOne")}</span>
            <h2>{t("dailyServices")}</h2>
            <p>{t("dailyServicesDescription")}</p>
          </div>
          <div className="desktop-services__grid">
            <Link to="/food"><i>🍔</i><div><h3>{t("restaurants")}</h3><p>{t("restaurantServiceDescription")}</p><b>{t("exploreBusinesses")} →</b></div></Link>
            <button type="button" disabled={!serviceMap.POINT_TO_POINT?.enabled} onClick={() => openService("POINT_TO_POINT")}><i>📦</i><div><h3>{t("pickupDelivery")}</h3><p>{t("pickupDescription")}</p><b>{t("requestDelivery")} →</b></div></button>
            <button type="button" disabled={!serviceMap.TAXI?.enabled} onClick={() => openService("TAXI")}><i>🚕</i><div><h3>Taxi</h3><p>{serviceMap.TAXI?.enabled ? t("taxiDescription") : t("temporarilyUnavailable")}</p><b>{serviceMap.TAXI?.enabled ? `${t("requestTaxi")} →` : t("verySoon")}</b></div></button>
          </div>
        </section>

        {featuredStores.length > 0 && (
          <section className="desktop-stores" id="negocios">
            <div className="desktop-stores__showcase">
              <div className="desktop-stores__header">
                <div>
                  <span>{t("buyLocal")}</span>
                  <h2>{t("meetBusinesses")}</h2>
                </div>
                {featuredStores.length > 5 && (
                  <div className="desktop-stores__controls">
                    <button type="button" aria-label={t("previousBusinesses")} onClick={() => scrollStores(-1)}>‹</button>
                    <button type="button" aria-label={t("nextBusinesses")} onClick={() => scrollStores(1)}>›</button>
                  </div>
                )}
              </div>
              <div className="desktop-stores__carousel" ref={storeCarouselRef} tabIndex="0">
                <div className="desktop-stores__track">
                  {carouselStores.map((store, index) => {
                    const duplicate = index >= featuredStores.length;
                    return (
                      <button
                        className="desktop-stores__card"
                        aria-hidden={duplicate || undefined}
                        tabIndex={duplicate ? -1 : 0}
                        type="button"
                        onClick={() => openDesktopApp(`/food/stores/${store.slug}`)}
                        key={`${store.slug}-${index}`}
                      >
                        <span><img src={store.image} alt="" loading="lazy" /></span>
                        <b>{store.name}</b>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </section>
        )}

        <section className="desktop-partners" id="unete">
          <div className="desktop-section-heading">
            <span>{desktopText("desktopPartnersEyebrow", t("growTogether"))}</span>
            <h2>{desktopText("desktopPartnersTitle", t("joinDelivery"))}</h2>
            <p>{desktopText("desktopPartnersDescription", t("joinDescription"))}</p>
          </div>
          <div className="desktop-partners__grid">
            <article><i>🏪</i><div><small>{t("forBusinesses")}</small><h3>{t("growSales")}</h3><p>{t("growSalesDescription")}</p><a href="/public/auth/store-registration">{t("registerBusiness")} →</a></div></article>
            <article><i>🛵</i><div><small>{t("forDrivers")}</small><h3>{t("earnDelivering")}</h3><p>{t("driverDescription")}</p><a href="/public/auth/delivery-registration">{t("applyDriver")} →</a></div></article>
          </div>
        </section>

        <footer className="desktop-portal__footer">
          <img src="/assets/img/logos/logo.png" alt="Delivery Huaraz" />
          <p>{t("footerDescription")}</p>
          <Link to="/food">{t("enterApp")} →</Link>
        </footer>
      </section>

      <div className="mobile-portal">
      <header className="portal__header">
        <Link to="/food" className="portal__logo" aria-label="Delivery Huaraz">
          <img src="/assets/img/logos/logo.png" alt="Delivery Huaraz" />
        </Link>
        <button className="portal__address" type="button" onClick={openAddress}>
          <span>{locating ? t("locating") : address?.address || t("selectAddress")}</span>
          <b aria-hidden="true">›</b>
        </button>
      </header>

      <div className="portal__content">
        <PromoBanner items={banners} />

        {!address && (
          <button className="gps-card" type="button" onClick={openAddress}>
            <span>{t("gpsPrompt")}</span>
            <b>{t("chooseAddress")}</b>
          </button>
        )}

        <section className="portal__intro">
          <small>{desktopText("portalIntroEyebrow", t("allInOne"))}</small>
          <h1>{desktopText("portalIntroTitle", t("exploreHuaraz"))}</h1>
          <p>{desktopText("portalIntroDescription", t("portalSub"))}</p>
        </section>

        <section className="service-grid service-grid--primary">
          <Link className="service-card service-card--food" to="/food">
            <span className="service-card__art">🍔</span>
            <strong>{t("restaurants")}</strong>
            <small>{t("orderFood")}</small>
          </Link>
          <button
            className="service-card service-card--delivery"
            type="button"
            disabled={!serviceMap.POINT_TO_POINT?.enabled}
            onClick={() => openService("POINT_TO_POINT")}
          >
            <span className="service-card__art">📦</span>
            <strong>{locale === "ES" ? serviceMap.POINT_TO_POINT?.name || t("pickupDelivery") : t("pickupDelivery")}</strong>
            <small>
              {serviceMap.POINT_TO_POINT?.enabled
                ? locale === "ES" ? serviceMap.POINT_TO_POINT?.description : t("pickupDescription")
                : locale === "ES" ? serviceMap.POINT_TO_POINT?.unavailable_message || t("temporarilyUnavailable") : t("temporarilyUnavailable")}
            </small>
          </button>
        </section>

        <section className="service-grid service-grid--secondary">
          <button
            className="service-card service-card--small"
            type="button"
            disabled={!serviceMap.TAXI?.enabled}
            onClick={() => openService("TAXI")}
          >
            <span>🚕</span>
            <strong>{serviceMap.TAXI?.name || "Taxi"}</strong>
            <small>{serviceMap.TAXI?.enabled ? t("available") : t("soon")}</small>
          </button>
          <article className="service-card service-card--small is-disabled">
            <span>🛒</span><strong>{t("shopping")}</strong><small>{t("soon")}</small>
          </article>
          <article className="service-card service-card--small is-disabled">
            <span>🎟️</span><strong>{t("events")}</strong><small>{t("soon")}</small>
          </article>
        </section>

        <section className="portal-partners">
          <div className="portal-partners__heading">
            <small>{t("growTogether")}</small>
            <h2>{t("joinDelivery")}</h2>
            <p>{t("mobileJoinDescription")}</p>
          </div>
          <div>
            <article>
              <span>🏪</span>
              <h3>{t("haveBusiness")}</h3>
              <p>{t("mobileBusinessDescription")}</p>
              <a href="/public/auth/store-registration">{t("registerBusiness")} →</a>
            </article>
            <article>
              <span>🛵</span>
              <h3>{t("haveMotorcycle")}</h3>
              <p>{t("mobileDriverDescription")}</p>
              <a href="/public/auth/delivery-registration">{t("applyDriver")} →</a>
            </article>
          </div>
        </section>
      </div>

      <BottomNav />
      </div>
      <AddressSheet />
    </main>
  );
}
